// Where the OTHER app lives, for links that cross between the main app
// (bigagri.app) and the admin app (admin.bigagri.app). They are different
// origins with different sessions, so these are full URLs, never routes.
//
// No fallback: a deploy that wasn't told where the main app is refuses the
// link rather than guessing, so a staging admin can never send an operator
// into production.
const MAIN_APP_URL: string | null =
  (import.meta.env.VITE_MAIN_APP_URL as string | undefined)?.replace(/\/+$/, '') || null
const ADMIN_APP_URL: string | null =
  (import.meta.env.VITE_ADMIN_APP_URL as string | undefined)?.replace(/\/+$/, '') || null

/** A page in the admin app, from the main app (the Dashboard's Platform
 *  button), or null when this deploy has no VITE_ADMIN_APP_URL — the button is
 *  then not shown, never pointed at a guessed host. */
export function adminAppUrl(path = '/', adminUrl: string | null = ADMIN_APP_URL): string | null {
  return adminUrl ? `${adminUrl}${path.startsWith('/') ? path : `/${path}`}` : null
}

/** Main-app paths whose pages moved to the admin app, each with the admin-app
 *  path that replaced it. Bookmarks still carry the old ones, and the service
 *  worker answers a navigation with the cached app before any server redirect
 *  could, so the main app redirects them itself (<MovedToAdminApp/>). */
export const MOVED_TO_ADMIN_APP: Readonly<Record<string, string>> = {
  '/platform':              '/',
  '/platform-audit':        '/platform-audit',
  '/platform-health':       '/platform-health',
  '/tenant-signups':        '/tenant-signups',
  '/support':               '/support',
  '/partner-keys':          '/partner-keys',
  '/tenant-modules':        '/tenant-modules',
  '/commodity-flows':       '/commodity-flows',
  '/worker-id-collisions':  '/worker-id-collisions',
  '/tenant-setup-health':   '/tenant-setup-health',
  '/tenant-implementation': '/tenant-implementation',
  '/help-analytics':        '/help-analytics',
}

/** The main app's support-view start page for one tenant, or null when this
 *  deploy has no VITE_MAIN_APP_URL. The page confirms before it starts anything
 *  (src/modules/shared/SupportViewStart.tsx). */
export function supportViewStartUrl(tenantId: string): string | null {
  return MAIN_APP_URL ? `${MAIN_APP_URL}/support-view?tenant=${encodeURIComponent(tenantId)}` : null
}

/** Where an auth link that arrived at the ADMIN app belongs, or null when this
 *  page load isn't one. invite-user (and the provision-* functions) build the
 *  invitee's link from the request's Origin, so an invite sent from the admin
 *  app lands on admin.bigagri.app/accept-invite, a page the admin app doesn't
 *  have. The invitee is joining a tenant, and accepts in the main app.
 *  The path, query and #fragment (where the invite token rides) go across
 *  unchanged. Must run BEFORE the Supabase client loads: it reads a token in
 *  the URL at start-up and would sign the invitee into the wrong app. */
export function adminAuthLinkTarget(loc: { pathname: string; search: string; hash: string }, mainAppUrl: string | null = MAIN_APP_URL): string | null {
  if (!mainAppUrl) return null
  const isInvitePath = loc.pathname.replace(/\/+$/, '') === '/accept-invite'
  const carriesAuthToken = /(?:^#|&)type=(?:invite|recovery|magiclink|signup)(?:&|$)/.test(loc.hash)
  if (!isInvitePath && !carriesAuthToken) return null
  return `${mainAppUrl}${isInvitePath ? loc.pathname : '/accept-invite'}${loc.search}${loc.hash}`
}
